import { getTheme, Icon, Stack } from "@fluentui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";

import { DocumentOrLineModal } from "./components/DocumentOrLineModal/DocumentOrLineModal";
import Sidebar from "./components/invoiceSidebar/sidebar";
import PdfViewer from "./components/PdfViewer";
import { SearchInput } from "./components/SearchInput";
import { IGetInvoicesResponse, Tariff } from "./interfaces/invoices-response";
import { resolveInvoiceSearchQueryType } from "./utils/resolveInvoiceSearchQueryType";

import { useApi } from "@/core/libs/api/react-query/useApi";
import useQueryParams from "@/hooks/useQueryParams";
import { formatDocumentOrPhone } from "@/utils/FormatDocumentOrPhone";
import { normalizeDocumentNumber } from "@/utils/NormalizeDocumentNumber";
import { validateDocumentNumber } from "@/utils/ValidateDocumentNumber";
import { validatePhoneNumber } from "@/utils/ValidatePhoneNumber";

export type TypeOfQueryParam = "document" | "lineNumber";

const theme = getTheme();

export default function InvoiceViewer() {
  const { execute } = useApi();
  const { queryParams, changeQueryParams } = useQueryParams();

  const [inputValue, setInputValue] = useState("");
  const [result, setResult] = useState<IGetInvoicesResponse>(null);
  const [searchError, setSearchError] = useState<string>(null);
  const [isFetching, setIsFetching] = useState(false);

  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number>(null);
  const [selectedTariff, setSelectedTariff] = useState<Tariff>(null);

  const [queryType, setQueryType] = useState<TypeOfQueryParam>(null);
  const [documentOrLineModal, setDocumentOrLineModal] = useState<{
    documentOrLineModalOpen: boolean;
    candidateInputValue?: string;
  }>({
    documentOrLineModalOpen: false
  });

  const clearSearchResult = useCallback(() => {
    setResult(null);
    setSearchError(null);
    setSelectedInvoiceId(null);
    setSelectedTariff(null);
  }, []);

  const resetSearchState = useCallback(() => {
    setInputValue("");
    setQueryType(null);
    clearSearchResult();
    setDocumentOrLineModal({ documentOrLineModalOpen: false });
  }, [clearSearchResult]);

  const enableGeneratePayment = queryType !== "lineNumber";

  const resolvedDocument = useMemo(() => {
    if (!result) return undefined;

    if (queryType === "document") return normalizeDocumentNumber(inputValue);

    if (queryType === "lineNumber") {
      const selectedInvoice = result.invoices?.find(
        invoice => invoice.id === selectedInvoiceId
      );
      const firstInvoice = result.invoices?.[0];

      return selectedInvoice?.clientDocument || firstInvoice?.clientDocument;
    }
  }, [result, queryType, inputValue, selectedInvoiceId]);

  const getInvoices = useCallback(
    async (value: string, type: TypeOfQueryParam) => {
      setQueryType(type);

      const response = await execute<IGetInvoicesResponse>({
        url: "invoices",
        method: "GET",
        params: { [type]: value },
        setIsFetching
      });

      if (response?.error) {
        setSearchError(
          response.error.message ||
            "Ocorreu um erro ao buscar as tarifas do cooperado."
        );
        setResult(null);

        return;
      }

      setSearchError(null);
      setResult(response.data);
      setSelectedTariff(null);
      setSelectedInvoiceId(null);
    },
    [execute]
  );

  useEffect(() => {
    const queryDocument = queryParams.document;
    const queryLineNumber = queryParams.lineNumber;
    const queryValue = queryDocument || queryLineNumber;

    if (!queryValue) {
      resetSearchState();

      return;
    }

    const type = queryDocument ? "document" : "lineNumber";
    const sanitizedQueryValue = normalizeDocumentNumber(queryValue);
    const isValidQueryValue =
      type === "document"
        ? validateDocumentNumber(sanitizedQueryValue)
        : validatePhoneNumber(sanitizedQueryValue);

    setInputValue(formatDocumentOrPhone(sanitizedQueryValue));

    if (!isValidQueryValue) {
      setQueryType(null);
      clearSearchResult();
      setSearchError(
        "O valor informado não é um CPF, CNPJ ou número de linha válido."
      );

      return;
    }

    clearSearchResult();

    void getInvoices(sanitizedQueryValue, type);
  }, [
    queryParams.document,
    queryParams.lineNumber,
    getInvoices,
    clearSearchResult,
    resetSearchState
  ]);

  const searchByQuery = (value: string, type: TypeOfQueryParam) => {
    const oppositeType = type === "document" ? "lineNumber" : "document";
    const isSameQuery =
      queryParams[type] === value && !queryParams[oppositeType];

    if (isSameQuery) {
      void getInvoices(value, type);

      return;
    }

    changeQueryParams({
      document: type === "document" ? value : undefined,
      lineNumber: type === "lineNumber" ? value : undefined
    });
  };

  const handleSearch = () => {
    const sanitized = normalizeDocumentNumber(inputValue);

    if (!sanitized) return;

    clearSearchResult();

    const type = resolveInvoiceSearchQueryType(sanitized);

    if (type === "ambiguous") {
      setDocumentOrLineModal({
        documentOrLineModalOpen: true,
        candidateInputValue: sanitized
      });

      return;
    }

    if (!type) {
      setSearchError(
        "O valor informado não é um CPF, CNPJ ou número de linha válido."
      );

      return;
    }

    searchByQuery(sanitized, type);
  };

  const handlePickInputType = (choice: TypeOfQueryParam) => {
    const { candidateInputValue } = documentOrLineModal;
    setDocumentOrLineModal({
      documentOrLineModalOpen: false,
      candidateInputValue: undefined
    });

    if (!candidateInputValue) return;

    searchByQuery(candidateInputValue, choice);
  };

  const handleClearSearch = () => {
    resetSearchState();
    changeQueryParams({ document: undefined, lineNumber: undefined });
  };

  const toggleInvoiceExpansion = (invoiceId: number) => {
    setSelectedTariff(null);
    setSelectedInvoiceId(prev => (prev === invoiceId ? null : invoiceId));
  };

  return (
    <Stack tokens={{ childrenGap: 12 }} styles={{ root: { width: "100%" } }}>
      <Helmet>
        <title>Consulta de Faturas - Libertas</title>
      </Helmet>

      <Stack as="header" horizontal styles={{ root: { alignItems: "center" } }}>
        <Icon
          iconName="PageShared"
          style={{ fontSize: theme.fonts.xLarge.fontSize, margin: "0 10px" }}
        />
        <h2 style={{ fontSize: theme.fonts.xLarge.fontSize, margin: 0 }}>
          Consulta de Faturas
        </h2>
      </Stack>

      <SearchInput
        isFetching={isFetching}
        searchValue={inputValue}
        setSearchValue={setInputValue}
        handleSearch={handleSearch}
        handleClearSearch={handleClearSearch}
        onDismissError={() => setSearchError(null)}
        error={searchError}
      />

      {result && (
        <Stack as="section" horizontal tokens={{ childrenGap: 10 }}>
          <Sidebar
            invoices={result?.invoices || []}
            expandedInvoices={selectedInvoiceId ? [selectedInvoiceId] : []}
            onToggleInvoice={toggleInvoiceExpansion}
            selectedTariff={selectedTariff}
            setSelectedTariff={setSelectedTariff}
            isLoading={isFetching}
            document={resolvedDocument}
            isGeneratePaymentEnabled={enableGeneratePayment}
          />

          <PdfViewer
            selectedTariff={selectedTariff}
            invoiceId={selectedInvoiceId ?? undefined}
            isLoading={isFetching}
            document={resolvedDocument}
            expandedInvoices={selectedInvoiceId ? [selectedInvoiceId] : []}
          />
        </Stack>
      )}

      <DocumentOrLineModal
        documentOrLineModalOpen={documentOrLineModal.documentOrLineModalOpen}
        handlePickInputType={handlePickInputType}
        setDocumentOrLineModal={setDocumentOrLineModal}
      />
    </Stack>
  );
}
